/**
 * Assistant de démarrage — carte "premiers pas" affichée UNIQUEMENT sur les
 * dossiers fraîchement créés qui n'ont ni étape avancée, ni qualification, ni
 * prochaine action planifiée.
 *
 * L'objectif : ne plus laisser un dossier vide sans direction. On propose
 * 3 actions maximum pour lancer le suivi.
 */

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Rocket, ClipboardList, Layers, CalendarPlus, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';

const EARLY_EP_STAGES = new Set([null, undefined, '', 'ep_prospection', 'ep_veille']);
const EARLY_CH_STAGES = new Set([null, undefined, '']);
const MAX_AGE_DAYS = 14;  // Assistant présent 14 jours max après création

function isFreshProspect(prospect) {
    if (!prospect) return false;
    // Étape module encore "prospection" ou nulle
    const epFresh = EARLY_EP_STAGES.has(prospect.easy_power_stage);
    const chFresh = EARLY_CH_STAGES.has(prospect.chantier_stage);
    if (!epFresh || !chFresh) return false;
    // Aucun formulaire de qualification Travaux d'optimisation démarré
    if (prospect.chantier_qualification_id || prospect.chantier_qualification_display_note) return false;
    // Créé il y a moins de MAX_AGE_DAYS jours
    if (prospect.created_at) {
        try {
            const ageDays = (Date.now() - new Date(prospect.created_at).getTime()) / (1000 * 60 * 60 * 24);
            if (ageDays > MAX_AGE_DAYS) return false;
        } catch { /* noop */ }
    }
    return true;
}

/**
 * @param {Object} prospect - dossier
 * @param {Function} onScheduleAction - callback : ouvre le dialog RDV/Tâche rapide
 */
export default function ProspectStarterAssistant({ prospect, onScheduleAction }) {
    const navigate = useNavigate();
    if (!isFreshProspect(prospect)) return null;

    const steps = [
        {
            key: 'qualify',
            icon: ClipboardList,
            title: 'Compléter la qualification',
            desc: "Renseigner les informations société, adresse, activité — c'est la base du dossier.",
            action: () => {
                // Scroll vers la section qualification
                const el = document.getElementById('section-qualif');
                if (el) {
                    const y = el.getBoundingClientRect().top + window.scrollY - 72;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                }
            },
            cta: 'Y aller',
        },
        {
            key: 'activate-module',
            icon: Layers,
            title: 'Activer un module',
            desc: 'Choisir si le dossier est un Courtage, des Travaux d’optimisation, ou les deux.',
            action: () => navigate(`/prospects/${prospect.id}/edit`),
            cta: 'Choisir',
        },
        {
            key: 'plan-action',
            icon: CalendarPlus,
            title: 'Planifier une action',
            desc: 'Ajouter un RDV, un rappel ou une tâche pour que le dossier ne dorme pas.',
            action: () => onScheduleAction && onScheduleAction(),
            cta: 'Programmer',
        },
    ];

    return (
        <Card
            className="border-2 border-indigo-200 bg-gradient-to-br from-indigo-50/50 via-white to-purple-50/40"
            data-testid="prospect-starter-assistant"
        >
            <CardContent className="p-4 sm:p-5">
                <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center shrink-0 shadow-sm">
                        <Rocket className="w-5 h-5 text-white" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="font-bold text-slate-900 text-sm sm:text-base">
                            Bienvenue sur ce nouveau dossier ! 🚀
                        </p>
                        <p className="text-xs sm:text-sm text-slate-500">
                            3 étapes pour bien le démarrer et ne rien oublier.
                        </p>
                    </div>
                </div>

                <div className="grid gap-2 sm:gap-3 grid-cols-1 md:grid-cols-3">
                    {steps.map((s) => {
                        const Icon = s.icon;
                        return (
                            <button
                                key={s.key}
                                type="button"
                                onClick={s.action}
                                data-testid={`starter-step-${s.key}`}
                                className={cn(
                                    "text-left rounded-lg border border-slate-200 bg-white p-3 sm:p-4",
                                    "hover:border-indigo-300 hover:shadow-sm transition-all",
                                    "flex flex-col gap-2"
                                )}
                            >
                                <div className="flex items-center gap-2">
                                    <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
                                        <Icon className="w-4 h-4" />
                                    </div>
                                    <p className="font-semibold text-slate-900 text-sm min-w-0 truncate">{s.title}</p>
                                </div>
                                <p className="text-[11px] sm:text-xs text-slate-500 leading-snug">{s.desc}</p>
                                <p className="text-xs font-medium text-indigo-600 mt-auto flex items-center gap-1">
                                    {s.cta} <ChevronRight className="w-3 h-3" />
                                </p>
                            </button>
                        );
                    })}
                </div>
            </CardContent>
        </Card>
    );
}
